import React, { useEffect, useState } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { BotoAjuda } from "./AjudaModal";
import AjudaRecursos from "./AjudaRecursos";
import { cridaRecurs } from "./RecursosComuns";

/**
 * Carcassa de la fitxa dels CRUD de Recursos (patró de Detallsproducte):
 * capçalera amb accions, banner de confirmació per desactivar/reactivar,
 * cos amb targetes numerades i dock amb navegació entre registres.
 *
 * Modes: nou | consultar | modificar | desactivar | reactivar.
 * El formulari concret (children) és de cada recurs; el desat del formulari
 * arriba per `onDesar` i el canvi d'estat (baixa lògica) es resol aquí.
 */
function FitxaRecursos({
  mode,
  record,
  llista,
  idDe,
  onNavigate,
  onCancel,
  onCanviarMode,
  onDesar,
  onEstatCanviat,
  estat, // { endpoint, idCamp, campActiu }
  esActiu,
  icona,
  nomSingular, // "vehicle"
  nomArticle, // "el vehicle" / "l'eina" / "el magatzem"
  femeni = false,
  subtitolNou,
  subtitolRegistre,
  pestanyaAjuda,
  children,
}) {
  const { rolUsuari, empresaSeleccionada } = useUserContext();
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [ocupat, setOcupat] = useState(false);

  TeclaEscEnter(mostraAjuda ? null : onCancel, null);

  useEffect(() => {
    setMenuAccions(false);
  }, [record, mode]);

  const esConfirmacio = mode === "desactivar" || mode === "reactivar";
  const esEditable = mode === "nou" || mode === "modificar";
  const actiu = record ? esActiu(record) : true;

  const llistaNav = Array.isArray(llista) ? llista : [];
  const indexActual = record ? llistaNav.findIndex((r) => idDe(r) === idDe(record)) : -1;
  const potNavegar = ["consultar", "modificar"].includes(mode) && llistaNav.length > 1 && indexActual >= 0;

  const anterior = () => onNavigate?.(llistaNav[indexActual === 0 ? llistaNav.length - 1 : indexActual - 1]);
  const seguent = () => onNavigate?.(llistaNav[indexActual === llistaNav.length - 1 ? 0 : indexActual + 1]);

  const confirmarEstat = async () => {
    if (ocupat || !record) return;
    const nouValor = mode === "reactivar" ? 1 : 0;
    setOcupat(true);
    const resposta = await cridaRecurs(estat.endpoint, {
      [estat.idCamp]: idDe(record),
      Actiu: nouValor,
      idEmpresa: empresaSeleccionada?.IdEmpresa || null,
    });
    setOcupat(false);
    if (resposta.ok) {
      onEstatCanviat?.({ [estat.campActiu]: nouValor });
    } else if (!resposta.silenci) {
      alert(`Error: ${resposta.message}`);
    }
  };

  const desar = async () => {
    if (ocupat) return;
    setOcupat(true);
    try {
      await onDesar?.();
    } finally {
      setOcupat(false);
    }
  };

  const titol =
    mode === "nou"
      ? `${femeni ? "Nova" : "Nou"} ${nomSingular}`
      : mode === "modificar"
      ? `Modificar ${nomSingular}`
      : mode === "desactivar"
      ? `Desactivar ${nomSingular}`
      : mode === "reactivar"
      ? `Reactivar ${nomSingular}`
      : `${nomSingular.charAt(0).toUpperCase()}${nomSingular.slice(1)} · Fitxa`;

  const subtitol = mode === "nou" ? subtitolNou : subtitolRegistre;
  const botoConfirmacio = mode === "reactivar" ? `Reactivar ${nomSingular}` : `Desactivar ${nomSingular}`;

  return (
    <form className="dv2-entity" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaRecursos pestanya={pestanyaAjuda} onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "desactivar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "desactivar" ? "🗑️" : icona}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitol}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title={`Com funciona la fitxa de ${nomSingular}`} onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && pucModificar && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className={`dv2-menu-item ${actiu ? "is-danger" : ""}`}
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.(actiu ? "desactivar" : "reactivar");
                    }}
                  >
                    {actiu ? "Desactivar" : "Reactivar"}
                  </button>
                </div>
              )}
            </div>
          )}
          {esEditable && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={desar} disabled={ocupat}>
              Desar
            </button>
          )}
          {esConfirmacio && (
            <button
              type="button"
              className={`dv2-btn ${mode === "desactivar" ? "dv2-btn--danger" : "dv2-btn--primary"}`}
              onClick={confirmarEstat}
              disabled={ocupat}
            >
              {botoConfirmacio}
            </button>
          )}
        </div>
      </div>

      {mode === "desactivar" && (
        <div className="dv2-banner dv2-banner--danger">
          {`Es desactivarà ${nomArticle}: deixarà d'aparèixer als llistats d'actius, però no s'esborra res i l'historial es conserva. Es pot reactivar en qualsevol moment.`}
        </div>
      )}
      {mode === "reactivar" && (
        <div className="dv2-banner dv2-banner--info">
          {`Es tornarà a activar ${nomArticle}.`}
        </div>
      )}

      <div className="dv2-entity-body">{children}</div>

      {(potNavegar || esEditable || esConfirmacio) && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anterior} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {indexActual + 1} / {llistaNav.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={seguent} title="Següent">
                ›
              </button>
            </div>
          )}
          {(esEditable || esConfirmacio) && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {esConfirmacio ? (
                <button
                  type="button"
                  className={`dv2-btn ${mode === "desactivar" ? "dv2-btn--danger" : "dv2-btn--primary"}`}
                  onClick={confirmarEstat}
                  disabled={ocupat}
                >
                  {botoConfirmacio}
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={desar} disabled={ocupat}>
                  {mode === "nou" ? `Desar ${nomSingular}` : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default FitxaRecursos;
